import { Disclosure } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ChevronUpIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  InformationCircleIcon,
  TruckIcon,
  LifebuoyIcon,
  CreditCardIcon,
  FilmIcon,
  CpuChipIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  CubeIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import NavBar from '../features/navbar/Navbar';
import Footer from '../features/common/Footer';
import { useLanguage } from '../context/LanguageContext';
import { useFaqTranslations } from '../context/faqTranslations';
import MainLayout from '../components/MainLayout';

// Animated Section Wrapper
const AnimatedSection = ({ children, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animated Grid Items
const AnimatedGridItem = ({ children, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      whileHover={{ y: -5 }}
    >
      {children}
    </motion.div>
  );
};

function FAQ() {
  const { language } = useLanguage();
  const t = useFaqTranslations(language);

  const faqSections = [
    {
      title: t.generalInformation,
      icon: InformationCircleIcon,
      faqs: [
        {
          question: t.q1,
          answer: t.a1,
        },
        {
          question: t.q2,
          answer: t.a2,
        },
        {
          question: t.q3,
          answer: t.a3,
        },
        {
          question: t.q4,
          answer: t.a4,
        },
        {
          question: t.q5,
          answer: t.a5,
        },
      ],
    },
    {
      title: t.deliveryActivation,
      icon: TruckIcon,
      faqs: [
        {
          question: t.q6,
          answer: t.a6,
        },
        {
          question: t.q7,
          answer: t.a7,
        },
        {
          question: t.q8,
          answer: t.a8,
        },
        {
          question: t.q9,
          answer: t.a9,
        },
      ],
    },
    {
      title: t.accountIssuesSupport,
      icon: LifebuoyIcon,
      faqs: [
        {
          question: t.q10,
          answer: t.a10,
        },
        {
          question: t.q11,
          answer: t.a11,
        },
      ],
    },
    {
      title: t.paymentPricing,
      icon: CreditCardIcon,
      faqs: [
        {
          question: t.q12,
          answer: t.a12,
        },
        {
          question: t.q13,
          answer: t.a13,
        },
        {
          question: t.q14,
          answer: t.a14,
        },
      ],
    },
    {
      title: t.streamingPlatforms,
      icon: FilmIcon,
      faqs: [
        {
          question: t.q15,
          answer: t.a15,
        },
        {
          question: t.q16,
          answer: t.a16,
        },
      ],
    },
    {
      title: t.aiProfessionalTools,
      icon: CpuChipIcon,
      faqs: [
        {
          question: t.q17,
          answer: t.a17,
        },
        {
          question: t.q18,
          answer: t.a18,
        },
        {
          question: t.q19,
          answer: t.a19,
        },
      ],
    },
    {
      title: t.educationPlatforms,
      icon: AcademicCapIcon,
      faqs: [
        {
          question: t.q20,
          answer: t.a20,
        },
      ],
    },
  ];

  const quickLinks = [
    {
      icon: ChatBubbleLeftIcon,
      title: t.contactUs,
      description: t.contactUsDesc,
      href: '/contact',
    },
    {
      icon: CubeIcon,
      title: t.browseProducts,
      description: t.browseProductsDesc,
      href: '/',
    },
    {
      icon: HomeIcon,
      title: t.backToHome,
      description: t.backToHomeDesc,
      href: '/home',
    },
  ];

  return (
    <MainLayout>
      <NavBar>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 py-14 sm:py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-400/30 text-blue-100 mb-6">
              <QuestionMarkCircleIcon className="w-8 h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white mb-4">
              {t.pageTitle}
            </h1>
            <p className="text-base sm:text-lg text-blue-50 max-w-2xl mx-auto font-light">
              {t.pageSubtitle}
            </p>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="space-y-10">
            {faqSections.map((section, sectionIndex) => (
              <AnimatedSection key={sectionIndex} delay={sectionIndex * 0.1}>
                <div className="space-y-4">
                  <motion.h2
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="text-xl sm:text-2xl font-light tracking-tight text-slate-900 mb-6 flex items-center gap-3"
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-500 text-white">
                      <section.icon className="w-4 h-4" />
                    </span>
                    {section.title}
                  </motion.h2>

                  <div className="space-y-3">
                    {section.faqs.map((faq, faqIndex) => (
                      <motion.div
                        key={faqIndex}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: faqIndex * 0.08, ease: "easeOut" }}
                        viewport={{ once: true, amount: 0.3 }}
                      >
                        <Disclosure>
                          {({ open }) => (
                            <>
                              <Disclosure.Button className="w-full bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-6 py-4 text-left transition-all duration-200 hover:border-blue-200 hover:shadow-subtle">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-slate-900 text-base pr-4">
                                    {faq.question}
                                  </span>
                                  <ChevronUpIcon
                                    className={`h-5 w-5 text-blue-500 transition-transform duration-200 flex-shrink-0 ${
                                      open ? '' : 'transform rotate-180'
                                    }`}
                                  />
                                </div>
                              </Disclosure.Button>

                              <Disclosure.Panel className="bg-blue-50 border-l-4 border-blue-500 px-6 py-4 mt-2 rounded-lg">
                                <motion.p
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ duration: 0.4 }}
                                  className="text-slate-700 leading-relaxed text-sm"
                                >
                                  {faq.answer}
                                </motion.p>
                              </Disclosure.Panel>
                            </>
                          )}
                        </Disclosure>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        {/* Still Have Questions Section */}
        <AnimatedSection>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
              className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-8 sm:p-12 relative overflow-hidden"
            >
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl" />
              </div>
              <div className="relative z-10">
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white mb-4">
                  {t.stillHaveQuestions}
                </h2>
                <p className="text-blue-100 text-base mb-8 font-light">
                  {t.contactSupport}
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    href="https://wa.me/92XXXXXXXXX"
                    className="flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-blue-600 font-medium py-4 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase"
                  >
                    <ChatBubbleLeftIcon className="h-5 w-5" />
                    {t.chatWhatsApp}
                  </motion.a>

                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    href="mailto:contact@subifyhub.com"
                    className="flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-blue-600 font-medium py-4 px-6 rounded-xl transition-all duration-200 text-sm tracking-wide uppercase"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                    {t.emailUs}
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>

        {/* Quick Links Section */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900 mb-10 text-center">
            {t.quickNavigation}
          </h2>

          <div className="grid sm:grid-cols-3 gap-5">
            {quickLinks.map((link, idx) => (
              <Link
                key={idx}
                to={link.href}
                className="group bg-white rounded-xl shadow-subtle hover:shadow-premium transition-all duration-300 p-6 text-center border border-slate-200 hover:border-blue-200"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-500 mb-4 group-hover:bg-blue-500 group-hover:text-white transition-colors duration-300">
                  <link.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {link.title}
                </h3>
                <p className="text-slate-600 text-sm">{link.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </NavBar>
    </MainLayout>
  );
}

export default FAQ;
